<template>
  <div class="f-h stats-content">
    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <div slot="shortcuts">
        <ChartPeriodSelector
          v-model="displayConfig"
          :preset-id="presetId"
          query-prefix="stats"
          :hide-preset-field="$store.state.device.isSmartphonePortrait"
          @presetPick="presetId = $event"
        />
      </div>
      <h2 class="basic-sub-title" style="text-align: left; background: transparent">
        {{ $t('message.FILTERS') }}
      </h2>
      <div>
        <ComponentsList
          ref="options"
          :value="computedStatsOptions"
          @edit="paramsEdit"
        />
      </div>
    </FiltersZone>
    <div
      class="f-h-below-filters stats-content__charts"
      :class="{ 'stats-content__charts--one-column v-centerer': !!statSelected && !isSmartphoneLandscape }"
    >
      <div class="stats-content__charts__wrapper">
        <div
          v-for="stat in displayedStats"
          :key="stat.key"
          class="stats-content__charts__wrapper__item"
          @click="onClickStat(stat.key)"
        >
          <div :class="{ 'f-h': true, selected: stat === statSelected }">
            <EllipsisDiv class="stats-content__charts__wrapper__item__title" :text="$t(stat.i18nLabel)" />
            <StatChart
              :value="stat.data"
              :type="stat.chartType"
              :labels="$store.state.device.isSmartphonePortrait || statsOptShowLabels"
              :values-on-chart="statsOptShowValues"
            />
          </div>
        </div>
      </div>
      <div v-if="statSelected" class="stats-content__charts__fullscreen-stat">
        <StatChart
          class="f-h"
          :value="statSelected.data"
          :type="statSelected.chartType"
          labels
          :values-on-chart="statsOptShowValues"
        />
        <CloseButton @click="statIdx = -1" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Stats page component.
 * Used in the basic pages of Shizen as well as in the management pages.
 * Display charts about Shizen usage, reported problems, created actions, problems distribution per type, ...
 *
 * @vue-prop {Boolean} [selectMgtTeams]  if set and true, add a teams selection option in the filters-zone
 */

import { isEqual } from 'lodash';
// Components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import CloseButton from '@/components/close-button.vue';
import ComponentsList from '@/components/components-list.vue';
import EllipsisDiv from '@/components/ellipsize.vue';
import FiltersZone from '@/components/filters-zone.vue';
import StatChart from '@/components/stat-chart.vue';
// Mixins
import mixinStatsOptions from '@/mixins/page-options/stats.vue';
// Utils
import { STAT_TYPES, TIME_RANGE_FORMAT } from '@/utils/constants';
import { calcPeriodStartDate } from '@/utils/main';
import { computeConfigStart } from '@/utils/indicator-utils';


export default {

  name: 'Stats',

  components: {
    ChartPeriodSelector,
    CloseButton,
    ComponentsList,
    EllipsisDiv,
    FiltersZone,
    StatChart,
  },

  mixins: [mixinStatsOptions],

  props: {
    selectMgtTeams: Boolean,
  },

  data() {
    return {
      displayConfig: this.$store.getters.defaultIndicDisplayConfig,
      /** Data fetched from getStats.php and reformatted */
      statsData: {},
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.STATS'),
    };
  },

  computed: {
    /** Array: Apply data from db in graphics stats */
    stats() {
      return STAT_TYPES.map(stat => Object.assign({ data: this.statsData[stat.key] }, stat));
    },
    /** Array: Filters zone options. See page-options/stats.vue : `statsOptions` */
    computedStatsOptions() {
      if (this.selectMgtTeams) {
        const idx = this.statsOptions.findIndex(opt => opt.key === 'statsCategoriesLegend');
        const opts = [...this.statsOptions];
        opts.splice(idx, 0, {
          key: 'statsOptTeamFilter',
          component: 'AdvancedTeamPicker',
          value: this.statsOptTeamFilter,
          options: {
            placeholder: this.$t('message.NONE_FEM'),
            useEditableLabel: true,
            legend: this.$t('message.TEAMS'),
            class: 'stats-content__team-filter-option',
            fieldTeamsNumber: true,
          },
        });
        return opts;
      }
      return this.statsOptions;
    },
    /** Array: Show graphics you choose */
    displayedStats() {
      return this.stats.filter(stat => this.statsOptSelectedCategories.includes(stat.category));
    },
    /** Array: Categories you selected */
    statsOptSelectedCategories() {
      const queryIds = this.$route.query.selectedCategories;
      if (queryIds === undefined) {
        return this.categoryItems.map(item => item.id);
      }
      return !queryIds ? [] : queryIds.split(',').map(e => Number(e));
    },
    /** Return the ids of types that should be selected */
    statsOptSelectedProblemsTypes() {
      const queryIds = this.$route.query.selectedTypes;
      return !queryIds || queryIds === '' ? [] : queryIds.split(',').map(t => Number(t));
    },
    /** Boolean: True if selected pb types' children should also be filtered in */
    statsOptSelectedPbTypesInclusive() {
      return this.$route.query.selectedTypesInclusive === 'true';
    },
    /** Return the ids of zones that should be selected */
    statsOptSelectedZones() {
      const queryIds = this.$route.query.statsSelectedZones;
      return !queryIds || queryIds === '' ? [] : queryIds.split(',').map(t => Number(t));
    },
    /** Boolean: True if selected zones' children should also be filtered in */
    statsOptSelectedZonesInclusive() {
      return this.$route.query.statsSelectedZonesInclusive === 'true';
    },
    // Fetch parameters for fetchStats
    fetchParams() {
      return {
        range: {
          start: this.range.start.format(TIME_RANGE_FORMAT),
          end: this.range.end.format(TIME_RANGE_FORMAT),
        },
        teams: this.selectMgtTeams && this.statsOptTeamFilter && this.statsOptTeamFilter.length
          ? this.statsOptTeamFilter
          : undefined,
        inclusiveTypes: this.statsOptSelectedPbTypesInclusive,
        selectedProblemsTypes: this.statsOptSelectedProblemsTypes,
        inclusiveZones: this.statsOptSelectedZonesInclusive,
        selectedZones: this.statsOptSelectedZones,
      };
    },
    /** Query string for a choosen chart */
    statIdx: {
      get() {
        return Number(this.$route.query.statSelected);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        if (STAT_TYPES[newVal]) {
          query.statSelected = String(newVal);
        } else {
          delete query.statSelected;
        }
        this.$router.replace({ query });
      },
    },
    /** Number; ID of the selected display config */
    presetId: {
      get() {
        return Number(this.$route.query.statsPresetId || 1);
      },
      set(newVal) {
        const query = Object.assign({}, { ...this.$route.query, statsPresetId: String(newVal) });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Stat displayed fullscreen */
    statSelected() {
      return this.stats[this.statIdx];
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.displayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj).startOf('day'),
        end: chartEnd.clone().endOf('day'),
      };
    },
    /** Object; See dmp/indicators.vue : allValuesRange */
    range() {
      if (!this.displayConfig.increasingValues) {
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.displayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart).startOf('day'), end: chartEnd.endOf('day') };
    },
    /** Boolean: Whether to display labels on the charts */
    statsOptShowLabels() {
      return this.$route.query.statsOptShowLabels === 'true';
    },
    /** Boolean: Whether to display values on the charts */
    statsOptShowValues() {
      return this.$route.query.statsOptShowValues === 'true';
    },
    /** Array: Selected team ids when selectMgtTeams is true */
    statsOptTeamFilter() {
      const { statsOptTeamFilter } = this.$route.query;
      if (!statsOptTeamFilter) {
        return this.$store.getters.allAccessibleTeams.map(t => t.id);
      }
      return statsOptTeamFilter.split(',').map(id => Number(id));
    },
    isSmartphoneLandscape() {
      return this.$store.state.device.isSmartphoneLandscape;
    },
  },

  watch: {
    fetchParams(nv, ov) {
      if (!isEqual(nv, ov)) {
        this.fetchStats();
      }
    },
    displayedStats() {
      // If current selected stat is filtered out, unselect it
      if (this.statSelected && !this.displayedStats.includes(this.statSelected)) {
        this.statIdx = -1;
      }
    },
  },

  created() {
    // Initialization
    this.refresh();
  },

  methods: {
    /** Refresh page content */
    refresh() {
      this.fetchStats();
    },
    /** Fetch, reformat and store stats data in statsData */
    fetchStats() {
      this.$http.post('/server/teams/getStats.php', this.fetchParams).then((response) => {
        const { data } = response;
        if (!(data instanceof Object)) {
          return;
        }
        this.statsData = this.statsFormatStatsData(data, this.displayConfig);
      });
    },
    /**
     * Event handler - Controls fullscreen stat displayed.
     * @param {string} statIdxa - Key name of the stat, find in STAT_TYPES
     */
    onClickStat(statKey) {
      const statIdx = STAT_TYPES.findIndex(stat => stat.key === statKey);
      if (statIdx === this.statIdx) {
        // Same stat is clicked ; Hide fullscreen stat
        this.statIdx = -1;
      } else {
        this.statIdx = statIdx;
      }
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'selectedCategories') {
        query.selectedCategories = option.value.length ? option.value.join(',') : '';
      } else if (option.key === 'selectedTypesInclusive') {
        query.selectedTypesInclusive = option.value ? 'true' : 'false';
      } else if (option.key === 'selectedTypes') {
        query.selectedTypes = option.value.length ? option.value.join(',') : undefined;
      } else if (option.key === 'statsSelectedZonesInclusive') {
        query.statsSelectedZonesInclusive = option.value ? 'true' : 'false';
      } else if (option.key === 'statsSelectedZones') {
        query.statsSelectedZones = option.value.length ? option.value.join(',') : undefined;
      } else if (option.key === 'statsOptShowLabels') {
        query.statsOptShowLabels = option.value ? 'true' : 'false';
      } else if (option.key === 'statsOptShowValues') {
        query.statsOptShowValues = option.value ? 'true' : 'false';
      } else if (option.key === 'statsOptTeamFilter') {
        query.statsOptTeamFilter = option.value.length ? option.value.join(',') : undefined;
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.stats-content {
  overflow: hidden;

  &__charts {
    position: relative;

    &.f-h-below-filters {
      padding: $page-content-padding;
      padding-right: 0px;
      padding-bottom: 0px;
      overflow: visible;
    }

    &__wrapper {
      display: flex;
      flex-wrap: wrap;
      align-content: flex-start;
      width: 100%;
      height: 100%;
      overflow: auto;

      &__item {
        box-sizing: border-box;
        cursor: pointer;
        display: block;
        vertical-align: top;
        min-height: 180px;
        height: calc(25% - #{$page-content-padding});
        max-height: calc(25% - #{$page-content-padding});
        flex-basis: calc(25% - #{$page-content-padding});
        flex-grow: 1;
        min-width: 0px;
        max-width: calc(25% - #{$page-content-padding});
        margin-right: $page-content-padding;
        margin-bottom: $page-content-padding;

        > div {
          background: #ffffff;
          box-sizing: border-box;
          padding: 1rem;
          height: 100%;
          border-radius: $main-border-radius;

          &:hover {
            background: $color-gray-background;
          }

          &.selected {
            background: $color-gray-background-hover;
          }

          &:active {
            box-shadow: $color-shizen-blue-btn-shadow;
          }
        }

        &__title {
          font-size: 0.8em;
          line-height: 1.15em;
          height: 2.5em;
          white-space: normal;
          text-align: center;
        }

        .stat-chart {
          height: calc(100% - 2.5em);
        }
      }
    }

    &__fullscreen-stat {
      height: calc(100% - #{$page-content-padding});
      flex: 1;
      margin: $page-content-padding;
      margin-top: 0px;
      background: white;
      border-radius: $main-border-radius;
      padding: 1rem 2.5rem 1rem 1rem;
      box-sizing: border-box;
      min-width: 0px;
      position: relative;
    }

    &--one-column {

      .stats-content__charts__wrapper {
        flex: 0 0 20%;
        width: 20%;
        height: 100%;
      }

      .stats-content__charts__wrapper__item {
        display: block;
        height: calc(25% - #{$page-content-padding});
        margin-bottom: $page-content-padding;
        flex-basis: 100%;
        max-width: 100%;
        margin-right: 0px;
      }
    }
  }

  &__team-filter-option {
    margin-bottom: 1.5rem;

    .editable-label .editable-label-text > * {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .stats-content {
    &__charts {
      &:not(.stats-content__charts--one-column) .stats-content__charts__wrapper__item {
        flex-basis: calc(50% - #{$page-content-padding});
        max-width: calc(50% - #{$page-content-padding});
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .stats-content {
    &__charts {
      .stats-content__charts__wrapper__item {
        height: 240px;
        max-height: 240px;
      }
      // Need to override properties from $smartphone-landscape
      &:not(.stats-content__charts--one-column) .stats-content__charts__wrapper__item {
        flex-basis: 100%;
        max-width: 100%;
      }
    }

    &__fullscreen-stat {
      display: none;
    }
  }
}

</style>
